import { MembershipLabel } from '../../models/common.model';
import type { Contract, Membership, Term, Tier } from '../models/membership.model';
import { ChangeType, ContractState } from '../models/membership.model';
import { humanize, isNonRenewingTerm } from './membership.formatters';

// Tree-shakeable helper functions

export function isTierCurrent(tier: Tier): boolean {
  return isTierActive(tier) || isTierFailed(tier);
}

export function isTierActive(tier: Tier): boolean {
  return (
    tier?.contractState === ContractState.ACTIVE || tier?.contractState === ContractState.PENDING
  );
}

export function isTierFailed(tier: Tier): boolean {
  return tier?.contractState === ContractState.PAYMENT_FAILED;
}

export function isContractFreeTier(contract: Contract | undefined): boolean {
  return contract?.tier?.freeTier ?? false;
}

/**
 * A paid one-time-purchase (non-renewing) membership. Free tiers are excluded
 * since they manage their own display separately. Used to hide recurring-only
 * UI (billing terms, next billing, upgrade/downgrade, payment method).
 */
export function isMembershipOneTimePurchase(membership: Membership | undefined): boolean {
  return (
    !!membership &&
    !isContractFreeTier(membership.contract) &&
    isNonRenewingTerm(membership.selectedTerm)
  );
}

export function isContractFailed(contract: Contract): boolean {
  return contract?.state === ContractState.PAYMENT_FAILED;
}

export function isMembershipOrTierSoldOut(membership: Membership, tier: Tier): boolean {
  return isMembershipSoldOut(membership) || isTierSoldOut(tier);
}

/**
 * Check if membership has reached max members
 */
export function isMembershipSoldOut(membership: Membership): boolean {
  const hasMaxMembers = !!membership?.maxMembers && membership.maxMembers > 0;
  const hasCurrentMembers = !!membership?.currentMembers && membership.currentMembers > 0;
  return hasMaxMembers && hasCurrentMembers && membership.currentMembers >= membership.maxMembers;
}

/**
 * Check if tier has reached max members
 */
export function isTierSoldOut(tier: Tier): boolean {
  const hasMaxMembers = !!tier?.maxMembers && tier.maxMembers > 0;
  const hasCurrentMembers = !!tier?.currentMembers && tier.currentMembers > 0;
  return hasMaxMembers && hasCurrentMembers && tier.currentMembers >= tier.maxMembers;
}

export function getChangeType(contractState: ContractState): ChangeType {
  switch (contractState) {
    case ContractState.UPGRADE:
      return ChangeType.UPGRADE;
    case ContractState.DOWNGRADE:
      return ChangeType.DOWNGRADE;
    default:
      return ChangeType.TERM_CHANGE;
  }
}

export function getMembershipLabel(membership: Membership): string {
  const label = membership?.accountSettings?.membershipLabel ?? MembershipLabel.MEMBERSHIP;
  return humanize(label);
}

export function isTierAvailable(tier: Tier): boolean {
  return !tier.contractState && !isTierSoldOut(tier);
}

/**
 * Check if tier allows changes (upgrade/downgrade)
 */
export function canChangeTier(tier: Tier): boolean {
  return (
    tier.contractState === ContractState.UPGRADE || tier.contractState === ContractState.DOWNGRADE
  );
}

export function tierHasPerks(tier: Tier): boolean {
  return !!tier.perks && tier.perks?.length > 0;
}

export function getMembershipForProduct(
  memberships: Membership[],
  productId: string,
  productVariantId?: string
): Membership | undefined {
  return memberships.find((membership) =>
    membership.tiers.some((tier) =>
      tier.terms
        .flatMap((term) => term.associatedProductVariants)
        .some(
          (variant) =>
            variant.sourceProductId === productId &&
            (!productVariantId || variant.sourceVariantId === productVariantId)
        )
    )
  );
}

export function getTierForProduct(
  memberships: Membership[],
  productId: string,
  productVariantId?: string
): Tier | undefined {
  return memberships
    .flatMap((membership) => membership.tiers)
    .find((tier) =>
      tier.terms
        .flatMap((term) => term.associatedProductVariants)
        .some(
          (variant) =>
            variant.sourceProductId === productId &&
            (!productVariantId || variant.sourceVariantId === productVariantId)
        )
    );
}

export function getTermForProductVariant(
  memberships: Membership[],
  productId: string,
  productVariantId: string
): Term | undefined {
  return memberships
    .flatMap((membership) => membership.tiers)
    .flatMap((tier) => tier.terms)
    .find((term) =>
      term.associatedProductVariants.some(
        (variant) =>
          variant.sourceProductId === productId && variant.sourceVariantId === productVariantId
      )
    );
}

export function getTermForTierAndProductVariant(
  tier: Tier,
  productId: string,
  productVariantId: string
): Term | undefined {
  return tier.terms.find((term) =>
    term.associatedProductVariants.some(
      (variant) =>
        variant.sourceProductId === productId && variant.sourceVariantId === productVariantId
    )
  );
}

export function isMembershipMaxMembersSpecified(membership: Membership): boolean {
  return !!membership && !!membership.maxMembers && membership.maxMembers > 0;
}

export function isMembershipCurrentMembersSpecified(membership: Membership): boolean {
  return !!membership && !!membership.currentMembers && membership.currentMembers > 0;
}

export function isTierMaxMembersSpecified(tier: Tier): boolean {
  return !!tier && !!tier.maxMembers && tier.maxMembers > 0;
}

export function isTierCurrentMembersSpecified(tier: Tier): boolean {
  return !!tier && !!tier.currentMembers && tier.currentMembers > 0;
}
